import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../api";
import { Avatar, Pill, Spinner, strengthColor, stageColor, fmtInTz, tzAbbr, MY_TZ } from "../components/ui-bits";
import { ActionMenu, ConfirmDialog } from "../components/crud";
import { PersonForm } from "../components/record-forms";
import { useToast } from "../hooks/use-toast";
import { ArrowLeft, Mail, Building2, MapPin, Phone, Linkedin, Clock, Send, Handshake, Paperclip } from "lucide-react";

export default function PersonDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [p, setP] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);

  useEffect(() => {
    Promise.all([api.person(id), api.companies()]).then(([person, companyList]) => {
      setP(person);
      setCompanies(companyList);
    }).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <Spinner />;
  if (!p) return <div className="p-6 text-[13px]">Person not found.</div>;

  const localTime = fmtInTz(new Date().toISOString(), p.timezone, { hour: "numeric", minute: "2-digit" });
  const save = async (values) => {
    const company = companies.find((item) => item.id === values.company_id);
    const updated = await api.updatePerson(id, { ...values, company: company?.name || "" });
    setP((current) => ({ ...current, ...updated }));
    setShowEdit(false);
    toast({ title: "Person updated", description: updated.name });
  };
  const remove = async () => {
    setDeleteBusy(true);
    try {
      await api.deletePerson(id);
      toast({ title: "Person deleted", description: p.name });
      navigate("/people");
    } catch (error) {
      toast({ title: "Could not delete person", description: error?.response?.data?.detail || "Please try again.", variant: "destructive" });
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <div className="p-6 max-w-[980px] mx-auto animate-in-up">
      <button onClick={() => navigate("/people")} className="inline-flex items-center gap-1 text-[13px] text-[hsl(240,4%,50%)] hover:text-[hsl(240,6%,10%)] mb-3"><ArrowLeft className="h-4 w-4" /> People</button>

      <div className="flex items-start gap-4">
        <Avatar text={p.avatar} brand size={56} />
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h1 className="text-[22px] font-semibold">{p.name}</h1>
            <Pill className={strengthColor[p.connection_strength] || strengthColor.Weak}>{p.connection_strength}</Pill>
          </div>
          <div className="text-[13.5px] text-[hsl(240,4%,48%)] mt-0.5">{p.job_title}</div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-2 text-[13px] text-[hsl(240,4%,48%)]">
            <div className="inline-flex flex-wrap items-center gap-2">
              <a href={`mailto:${p.email}`} className="inline-flex items-center gap-1.5 text-[hsl(245,50%,50%)]"><Mail className="h-3.5 w-3.5" />{p.email}</a>
              {p.email && <button type="button" onClick={() => navigate(`/mailbox?compose=${encodeURIComponent(p.id)}`)} className="inline-flex items-center gap-1.5 rounded-md border border-[hsl(245,58%,84%)] bg-[hsl(245,58%,98%)] px-2 py-1 text-[12px] font-medium text-[hsl(245,50%,48%)] hover:bg-[hsl(245,58%,95%)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[hsl(245,58%,60%)]"><Send className="h-3.5 w-3.5" />Send email</button>}
            </div>
            {p.company && <button onClick={() => p.company_id && navigate(`/companies/${p.company_id}`)} className="inline-flex items-center gap-1.5 hover:text-[hsl(245,50%,50%)]"><Building2 className="h-3.5 w-3.5" />{p.company}</button>}
            <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{p.location}</span>
            <span className="inline-flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" />{p.phone}</span>
            {p.linkedin && <span className="inline-flex items-center gap-1.5"><Linkedin className="h-3.5 w-3.5" />{p.linkedin}</span>}
          </div>
          <div className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-[hsl(240,5%,96%)] px-2 py-1 text-[12px] text-[hsl(240,4%,45%)]">
            <Clock className="h-3.5 w-3.5" /> {p.timezone} · currently {localTime} local ({tzAbbr(p.timezone)})
          </div>
        </div>
        <ActionMenu onEdit={() => setShowEdit(true)} onDelete={() => setShowDelete(true)} />
      </div>

      {p.notes && <div className="mt-4 rounded-xl border border-[hsl(240,6%,91%)] bg-[hsl(240,20%,99%)] px-4 py-3 text-[13px] text-[hsl(240,5%,30%)]">{p.notes}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        {/* Sequences */}
        <div className="rounded-xl border border-[hsl(240,6%,91%)] bg-white">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-[hsl(240,6%,92%)]"><Send className="h-4 w-4 text-[hsl(240,4%,45%)]" /><h2 className="text-[14px] font-semibold">Sequences</h2><span className="text-[12px] text-[hsl(240,4%,55%)]">({(p.enrollments || []).length})</span></div>
          <div className="divide-y divide-[hsl(240,6%,94%)]">
            {(p.enrollments || []).length === 0 && <div className="px-4 py-6 text-[13px] text-[hsl(240,4%,55%)]">Not in any sequence.</div>}
            {(p.enrollments || []).map((e) => (
              <button key={e.id} onClick={() => navigate(`/sequences/${e.sequence_id}`)} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[hsl(240,20%,99%)] text-left">
                <div className="min-w-0 flex-1"><div className="text-[13.5px] font-medium truncate">{e.sequence_name}</div><div className="text-[12px] text-[hsl(240,4%,50%)]">{e.steps.length} steps · {e.steps.filter((s) => s.status === "sent").length} sent</div></div>
                <Pill className={e.status === "replied" ? "bg-blue-100 text-blue-700" : "bg-emerald-100 text-emerald-700"}>{e.status}</Pill>
              </button>
            ))}
          </div>
        </div>

        {/* Deals */}
        <div className="rounded-xl border border-[hsl(240,6%,91%)] bg-white">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-[hsl(240,6%,92%)]"><Handshake className="h-4 w-4 text-[hsl(240,4%,45%)]" /><h2 className="text-[14px] font-semibold">Deals at {p.company || "company"}</h2></div>
          <div className="divide-y divide-[hsl(240,6%,94%)]">
            {(p.deals || []).length === 0 && <div className="px-4 py-6 text-[13px] text-[hsl(240,4%,55%)]">No deals.</div>}
            {(p.deals || []).map((d) => (
              <button key={d.id} onClick={() => navigate("/deals")} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[hsl(240,20%,99%)] text-left">
                <div className="min-w-0 flex-1"><div className="text-[13.5px] font-medium truncate">{d.name}</div><div className="text-[12px] text-[hsl(240,4%,50%)]">Close {d.close_date}</div></div>
                <Pill className={stageColor[d.stage] || stageColor.Discovery}>{d.stage}</Pill>
                <span className="text-[13px] font-medium tabular">{d.value}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Mailbox conversations */}
      <div className="mt-4 rounded-xl border border-[hsl(240,6%,91%)] bg-white">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-[hsl(240,6%,92%)]"><Mail className="h-4 w-4 text-[hsl(240,4%,45%)]" /><h2 className="text-[14px] font-semibold">Email conversations</h2><span className="text-[12px] text-[hsl(240,4%,55%)]">({(p.email_conversations || []).length})</span></div>
        <div className="divide-y divide-[hsl(240,6%,94%)]">
          {(p.email_conversations || []).length === 0 && <div className="px-4 py-6 text-[13px] text-[hsl(240,4%,55%)]">No mailbox conversations yet.</div>}
          {(p.email_conversations || []).map((conversation) => (
            <div key={conversation.id} className="px-4 py-3">
              <div className="flex flex-wrap items-center gap-2"><span className="text-[13.5px] font-medium">{conversation.subject || "(no subject)"}</span>{conversation.sequence_name && <Pill className="bg-[hsl(245,58%,96%)] text-[hsl(245,50%,48%)]">{conversation.sequence_name}</Pill>}<span className="text-[11.5px] text-[hsl(240,4%,55%)]">{conversation.message_count || 0} messages</span><span className="ml-auto text-[11.5px] text-[hsl(240,4%,55%)]">{conversation.last_message_at ? fmtInTz(conversation.last_message_at, MY_TZ) + " IST" : ""}</span></div>
              <div className="mt-2 space-y-2">
                {(conversation.messages || []).map((message) => (
                  <div key={message.id} className={`rounded-lg px-3 py-2 ${message.direction === "outbound" ? "bg-[hsl(245,58%,97%)]" : "bg-[hsl(240,20%,99%)]"}`}>
                    <div className="flex items-center gap-2 text-[11px] text-[hsl(240,4%,52%)]"><span className="font-semibold text-[hsl(240,5%,32%)]">{message.direction === "outbound" ? "You" : (message.from_name || message.from_email || p.name)}</span><span>·</span><span>{message.created_at ? fmtInTz(message.created_at, MY_TZ) + " IST" : ""}</span></div>
                    <p className="mt-1 whitespace-pre-wrap text-[12.5px] leading-relaxed text-[hsl(240,5%,30%)]">{message.body_text || "(No text body)"}</p>
                    {(message.attachments || []).length > 0 && <div className="mt-1 flex flex-wrap gap-2">{message.attachments.map((attachment, index) => <span key={attachment.id || `${attachment.filename}-${index}`} className="inline-flex items-center gap-1 rounded-md border border-[hsl(240,6%,88%)] bg-white px-1.5 py-1 text-[10.5px] text-[hsl(240,4%,45%)]"><Paperclip className="h-3 w-3" />{attachment.filename || "Attachment"}</span>)}</div>}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Emails sent */}
      <div className="mt-4 rounded-xl border border-[hsl(240,6%,91%)] bg-white">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-[hsl(240,6%,92%)]"><Mail className="h-4 w-4 text-[hsl(240,4%,45%)]" /><h2 className="text-[14px] font-semibold">Emails sent</h2><span className="text-[12px] text-[hsl(240,4%,55%)]">({(p.emails || []).length})</span></div>
        <div className="divide-y divide-[hsl(240,6%,94%)]">
          {(p.emails || []).length === 0 && <div className="px-4 py-6 text-[13px] text-[hsl(240,4%,55%)]">No emails sent yet.</div>}
          {(p.emails || []).map((em, i) => (
            <div key={i} className="px-4 py-3">
              <div className="flex items-center gap-2"><span className="text-[13.5px] font-medium">{em.subject}</span><span className="text-[11.5px] text-[hsl(240,4%,55%)]">· {em.sequence}</span><span className="ml-auto text-[11.5px] text-[hsl(240,4%,55%)]">{em.sent_at ? fmtInTz(em.sent_at, MY_TZ) + " IST" : em.scheduled_date}</span></div>
              <p className="text-[12.5px] text-[hsl(240,5%,35%)] mt-1 whitespace-pre-wrap line-clamp-3">{em.body}</p>
            </div>
          ))}
        </div>
      </div>

      <PersonForm open={showEdit} onClose={() => setShowEdit(false)} onSubmit={save} initialValues={p} companies={companies} />
      <ConfirmDialog open={showDelete} onClose={() => !deleteBusy && setShowDelete(false)} onConfirm={remove} busy={deleteBusy} title="Delete person?" description={`“${p.name}” will be permanently removed.`} />
    </div>
  );
}
